import mitt from 'mitt';

// 定义算力提醒事件数据类型 - 支持两种格式
interface PowerReminderEventData {
  // UI显示相关字段
  title?: string;
  description?: string;
  buttonTempText?: string;
  buttonUpgradeText?: string;
  payModalType?: string;
}

type Events = {
  favoriteUpdate: any;
  switchIndex: number;
  startPhotoTweet: number;
  'power-severe-reminder': PowerReminderEventData;
  /** 向聊天输入框添加外部图片 */
  'chatInput:addImages': string[];
  /** 批量任务已提交 */
  'batchTaskSubmitted': void;
  // 增加通用事件
  [key: string]: any;
}

const $bus = mitt<Events>();

/**
 * 增强的事件总线，提供更好的开发体验
 */
class EnhancedEventBus {
  private bus = $bus;
  private eventCounts = new Map<string, number>();
  private lastEvents = new Map<string, { payload: any; timestamp: number }>();

  /**
   * 发送事件
   */
  emit<T extends keyof Events>(eventName: T, payload: Events[T]): void {
    // 记录事件统计
    const count = this.eventCounts.get(eventName as string) || 0;
    this.eventCounts.set(eventName as string, count + 1);
    
    // 记录最后的事件
    this.lastEvents.set(eventName as string, {
      payload,
      timestamp: Date.now()
    });

    // 开发环境日志
    if (process.env.NODE_ENV === 'development') {
      console.log(`📡 发送事件 ${String(eventName)}:`, {
        payload,
        count: count + 1,
        timestamp: new Date().toISOString()
      });
    }

    this.bus.emit(eventName, payload);
  }

  /**
   * 监听事件
   */
  on<T extends keyof Events>(eventName: T, handler: (payload: Events[T]) => void): void {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🎧 注册监听器 ${String(eventName)}`);
    }

    this.bus.on(eventName, handler);
  }

  /**
   * 取消监听
   */
  off<T extends keyof Events>(eventName: T, handler: (payload: Events[T]) => void): void {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🛑 移除监听器 ${String(eventName)}`);
    }

    this.bus.off(eventName, handler);
  }

  /**
   * 获取事件统计信息
   */
  getEventStats(): {
    eventCounts: Record<string, number>;
    recentEvents: Record<string, { payload: any; timestamp: number; timeAgo: string }>;
  } {
    const now = Date.now();
    const eventCounts: Record<string, number> = {};
    const recentEvents: Record<string, { payload: any; timestamp: number; timeAgo: string }> = {};

    this.eventCounts.forEach((count, eventName) => {
      eventCounts[eventName] = count;
    });

    this.lastEvents.forEach((event, eventName) => {
      const timeAgo = `${Math.round((now - event.timestamp) / 1000)}s ago`;
      recentEvents[eventName] = {
        ...event,
        timeAgo
      };
    });

    return { eventCounts, recentEvents };
  }

  /**
   * 清理统计信息
   */
  clearStats(): void {
    this.eventCounts.clear();
    this.lastEvents.clear();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 事件统计信息已清理');
    }
  }

  /**
   * 获取原始mitt实例（用于兼容性）
   */
  get raw() {
    return this.bus;
  }
}

// 创建增强的事件总线实例
const enhancedBus = new EnhancedEventBus();

// 导出原始的mitt实例（向后兼容）
export { $bus };

// 导出增强的事件总线
export { enhancedBus as $enhancedBus };

// 在开发环境中提供全局访问
if (process.env.NODE_ENV === 'development') {
  (window as any).__eventBus__ = enhancedBus;
  console.log('🚀 增强事件总线已注册到 window.__eventBus__');
}
